// The side of cropping that touches pixels. Images go through a canvas; video goes through
// ffmpeg.wasm, which is imported and loaded only the first time someone crops a video (its
// ~30 MB core comes from a CDN rather than the app bundle).

import { centeredRect, roundRect, outputSize, withExt } from './crop';

const FFMPEG_CORE = 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.10/dist/esm';

// Storage only accepts these for images; anything else (gif, tiff…) comes back as JPEG.
const IMAGE_OUT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

export async function cropImage(srcUrl, rect, out) {
  const res = await fetch(srcUrl);
  if (!res.ok) throw new Error(`Couldn't load the image (${res.status})`);
  const blob = await res.blob();
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = out.w; canvas.height = out.h;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, rect.x, rect.y, rect.w, rect.h, 0, 0, out.w, out.h);
  bitmap.close?.();
  const type = IMAGE_OUT[blob.type] ? blob.type : 'image/jpeg';
  const result = await new Promise(r => canvas.toBlob(r, type, 0.92));
  if (!result) throw new Error('Couldn\'t encode the cropped image');
  return { blob: result, ext: IMAGE_OUT[type] };
}

let ffmpegPromise = null;
function getFFmpeg() {
  if (!ffmpegPromise) {
    ffmpegPromise = (async () => {
      // @ffmpeg/ffmpeg is served unbundled from public/ffmpeg (scripts/copy-ffmpeg-worker.js):
      // webpack breaks the way its worker loads the core. Its worker resolves next to it.
      const [{ FFmpeg }, { toBlobURL }] = await Promise.all([
        import(/* webpackIgnore: true */ `${window.location.origin}/ffmpeg/index.js`),
        import('@ffmpeg/util'),
      ]);
      const ff = new FFmpeg();
      await ff.load({
        coreURL: await toBlobURL(`${FFMPEG_CORE}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${FFMPEG_CORE}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      return ff;
    })().catch(err => { ffmpegPromise = null; throw err; });
  }
  return ffmpegPromise;
}

/**
 * Crops (rect, in even source pixels), scales (out) and trims (start/end, seconds) a video in
 * one re-encode to H.264/AAC mp4 — the format Shopify handles everywhere. onStage reports
 * 'loading' (fetching ffmpeg + the file) then 'encoding'; onProgress gets 0–1 while encoding.
 */
export async function cropVideo(srcUrl, rect, out, { start = 0, end = null } = {}, { onStage, onProgress } = {}) {
  onStage?.('loading');
  const ff = await getFFmpeg();
  const { fetchFile } = await import('@ffmpeg/util');
  const inName = 'in' + (srcUrl.match(/\.(mp4|mov|m4v|webm)(?:\?|$)/i)?.[0].replace(/\?$/, '') || '.mp4');
  await ff.writeFile(inName, await fetchFile(srcUrl));

  const handler = ({ progress }) => onProgress?.(Math.min(1, Math.max(0, progress)));
  ff.on('progress', handler);
  onStage?.('encoding');
  try {
    const trim = [];
    if (start > 0) trim.push('-ss', start.toFixed(3));
    if (end != null) trim.push('-t', Math.max(0.1, end - start).toFixed(3));
    const code = await ff.exec([
      ...trim, '-i', inName,
      '-vf', `crop=${rect.w}:${rect.h}:${rect.x}:${rect.y},scale=${out.w}:${out.h}`,
      '-c:v', 'libx264', '-preset', 'veryfast', '-crf', '20', '-pix_fmt', 'yuv420p',
      '-c:a', 'aac', '-b:a', '128k', '-movflags', '+faststart',
      'out.mp4',
    ]);
    if (code !== 0) throw new Error('Video encode failed — the file may use a codec ffmpeg can\'t read');
    const data = await ff.readFile('out.mp4');
    return { blob: new Blob([data.buffer], { type: 'video/mp4' }), ext: 'mp4' };
  } finally {
    ff.off('progress', handler);
    await ff.deleteFile(inName).catch(() => {});
    await ff.deleteFile('out.mp4').catch(() => {});
  }
}

// Natural pixel size of an image or video, from its URL — the bulk crop needs it to centre a
// preset before anything is decoded for real.
export function mediaSize(url, isVideo) {
  return new Promise((resolve, reject) => {
    if (isVideo) {
      const v = document.createElement('video');
      v.preload = 'metadata'; v.muted = true;
      v.onloadedmetadata = () => resolve({ W: v.videoWidth, H: v.videoHeight, duration: v.duration });
      v.onerror = () => reject(new Error('Couldn\'t read the video'));
      v.src = url;
    } else {
      const img = new Image();
      img.onload = () => resolve({ W: img.naturalWidth, H: img.naturalHeight });
      img.onerror = () => reject(new Error('Couldn\'t load the image'));
      img.src = url;
    }
  });
}

// Centres `preset` on one media item (working from its original, if it has one) and returns
// the cropped file plus the crop settings to store — what "Crop all" runs per item.
export async function cropToPreset(m, preset, { isVideo, ratio, onProgress } = {}) {
  const src = m.original || m;
  const { W, H } = await mediaSize(src.url, isVideo);
  const rect = roundRect(centeredRect(W, H, ratio), isVideo);
  const out = outputSize(rect, preset, isVideo);
  const res = isVideo ? await cropVideo(src.url, rect, out, {}, { onProgress }) : await cropImage(src.url, rect, out);
  const filename = withExt(src.filename, res.ext);
  return { file: new File([res.blob], filename, { type: res.blob.type }), crop: { presetId: preset.id, rect, out } };
}
